<template>
    <div class="receipt">
        <header class="mb-4" v-if="order.invoice_number">
            <div class="row no-gutters">
                <div class="col-md">
                    <div class="px-4 py-2 pt-3 p-md-4">
                        <div v-if="order.id">
                            <h2 class="heading h4 mb-0">
                                Order ID: {{ order.id }}
                                <br />
                                <small class="text-muted">Invoice #{{order.invoice_number}}</small>
                            </h2>
                        </div>
                    </div>
                </div>
                <div class="col-md-3 ml-auto">
                    <div class="px-4 py-2 p-md-4">
                        <div v-if="order.status">
                            <dt>
                                Status
                            </dt>

                            <dd v-text="order.status"></dd>
                        </div>
                    </div>
                </div>
            </div>
        </header>
        <h2 v-else>
            Order Details
        </h2>

        <div class="mb-4">
            <div class="row">
                <div class="col-md col-pr">
                    <dl>
                        <dt>Customer</dt>
                        <dd>
                            <address class="m-0">
                                <b v-text="order.customer_name"></b>
                                <b v-if="order.user && order.user.phone" v-text="order.user.phone"></b>
                                <b v-if="zipCode && zipCode.state" v-html="`${order.street}<br>${zipCode.city}, ${zipCode.state.name} ${zipCode.zip_code}`">
                                    </b>
                            </address>
                        </dd>
                    </dl>
                </div>

                <div class="col-md-5 col-pr">

                    <dl class="row mb-0">
                        <dt class="col-6 col-md-5 text-md-right">Due Date</dt>
                        <dd class="col">
                            {{ shortDate($root.estimate.delivery_date) }},
                            {{ order.delivery_method }} Delivery
                        </dd>
                    </dl>

                </div>
            </div>
        </div>


        <div class="row">
            <div class="col-md col-pr">
                <div class="d-flex">
                    <h3>
                        Delivery Details
                    </h3>
                    <div class="ml-auto" v-if="allowChanges">
                        <router-link class="btn btn-link p-0" :to="{ name: 'form' }">Make Changes</router-link>
                    </div>
                </div>
                <div class="card mb-4 py-0">
                    <div class="card-body">
                        <div>
                            <div>
                                <dl class="row mb-0">
                                    <dt class="col-6">Address</dt>
                                    <dd class="col-6" v-text="order.street"></dd>
                                    <dt class="col-6">Cross Street</dt>
                                    <dd class="col-6" v-text="order.cross_street"></dd>
                                    <dt class="col-6">City</dt>
                                    <dd class="col-6" v-if="zipCode"
                                        v-text="`${zipCode.city}, ${zipCode.state.name} ${zipCode.zip_code}`"></dd>

                                    <dt class="col-6">Property Type</dt>
                                    <dd class="col-6">{{ order.property_type }}</dd>
                                    <dt class="col-6">Fill Pipe Location</dt>
                                    <dd class="col-6">{{ order.location }}</dd>
                                    <dt class="col-6">Tank Size</dt>
                                    <dd class="col-6">{{ showTankSize }}</dd>
                                    <dt class="col-6">Gauge Reading</dt>
                                    <dd class="col-6">{{ showGaugeReading }}</dd>
                                    <dt class="col-6">Gallons Ordered</dt>
                                    <dd class="col-6">{{ order.fill_tank ? `Fill Tank (${order.gallons})` : order.gallons }}</dd>

                                    <dt class="col-6">Price Per Gallon</dt>
                                    <dd class="col-6" v-text="usd3(gallonPrice)"></dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-md col-pr">
                <h3>Payment</h3>
                <div class="card mb-4 py-0">
                    <div class="card-body">
                        <div>
                            <div>
                                <dl class="row mb-0">
                                    <dt class="col-6">Name on Card</dt>
                                    <dd class="col-6">{{ order.first_name_on_card }} {{ order.last_name_on_card }}</dd>

                                    <dt class="col-6">Oil Subtotal</dt>
                                    <dd class="col-6">{{ usd(oilSubTotal) }}</dd>

                                    <template v-if="emergencyFee">
                                        <dt class="col-6">Emergency Fee</dt>
                                        <dd class="col-6">
                                            <span v-text="usd(emergencyFee)"></span>
                                        </dd>
                                    </template>

                                    <template v-if="primeFee">
                                        <dt class="col-6">Prime Fee</dt>
                                        <dd class="col-6">
                                            <span v-text="usd(primeFee)"></span>
                                        </dd>
                                    </template>

                                    <template v-if="small_order_fee">
                                        <dt class="col-6">Small Order Fee</dt>
                                        <dd class="col-6">
                                            <span v-text="usd(small_order_fee)"></span>
                                        </dd>
                                    </template>

                                    <dt class="col-6">Fees Subtotal</dt>
                                    <dd class="col-6">{{ usd(fee_total) }}</dd>

                                    <dt class="col-6">Tax</dt>
                                    <dd class="col-6">
                                        <span v-text="usd(tax)"></span>
                                    </dd>

                                    <dt class="col-6 h4 mt-2 mb-0 pt-3 border border-mid border-bottom-0 border-left-0 border-right-0">Grand Total</dt>
                                    <dd class="col-6 h4 mt-2 mb-0 pt-3 border border-mid border-bottom-0 border-left-0 border-right-0" v-text="usd(total)"></dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import moment from 'moment';
import numeral from 'numeral';

export default {
    props: ['allowChanges'],
    computed: {

        showTankSize() {
            return (this.order.tank_size_unknown) ? 'Unknown (275 gallons)' : this.order.tank_size + ' gallons';
        },

        showGaugeReading() {
            return (this.order.gauge_reading) ? this.order.gauge_reading : 'Unknown';
        },

        order() {
            return this.$root.order;
        },

        inProgress() {
            return (this.order.transaction_id) ? false : true;
        },

        specialInstructions() {
            return this.order['special_instructions'];
        },

        street() {
            return this.order.street;
        },

        zipCode() {
            return this.$root.zipCode;
        },

        cityStateZip() {
            return this.zipCode && `${this.zipCode.city}, ${this.zipCode.state.name}, ${this.zipCode.zip_code}`;
        },

        estimate() {
            return this.$root.estimate;
        },

        gallonPrice() {
            return this.$root.zipCode
                && this.$root.zipCode.zone
                && numeral(this.$root.zipCode.zone.price).format('0.000');
        },

        orderFee() {
            return this.estimate['order_fee'];
        },

        deliveryFee() {
            return this.estimate['delivery_fee'];
        },

        emergencyFee() {
            return this.estimate['emergency_fee'];
        },

        primeFee() {
            return this.estimate['prime_fee'];
        },

        subtotal() {
            return this.estimate['pre_tax_total'];
        },

        tax() {
            if (!this.estimate['tax'] || this.estimate['tax'] == 0) {
                return '';
            }
            return this.estimate['tax'];
        },

        showSubtotal() {
            if (this.subtotal > this.orderFee) {
                return true;
            }
            return false;
        },

        total() {
            return this.estimate['total'];
        },

        deliveryDate() {
            return moment(this.estimate['delivery_date']).format('dddd, MMMM Do');
        },

        fee_total() {
            return (numeral(this.emergencyFee).value() || 0)
                + (numeral(this.small_order_fee).value() || 0)
                + (numeral(this.primeFee).value() || 0);
        },

        tax() {
            return Math.round(this.oilSubTotal * this.order.tax_rate) / 100;
        },

        total() {
            return this.oilSubTotal + this.fee_total + this.tax;
        },

        oilSubTotal() {

            return numeral(this.gallonPrice).multiply(this.order.gallons).value();
        },

        small_order_fee() {
            if (this.order.gallons < 100) {
                return this.$root.zipCode.zone.small_order_fee;
            } else if (this.order.gallons < 150) {
                return this.$root.zipCode.zone.medium_order_fee;
            }
            return 0;
        },
    }
}
</script>
